import {
  makeWainscotingCrossSections,
  makeWainscotingDisjointCrossSections,
} from "building-generation/elements/wainscoting";
import type { Building } from "building-types/building/buildingTypes";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { sheetPolylineGeometryNew } from "building-types/views/sheetGeometryTypes";
import { FLOOR_PLAN_CUT_PLANE } from "common/constants/buildingObjects";
import type { PlanGenerationContext } from "doc/plans/planGeometrySource";
import { wainscotingHatchInlineStyle, wainscotingMaskInlineStyle } from "doc/plans/planStyleSheets";

function isWainscotingAboveCutPlane(building: Building, cutPlaneHeight?: number): boolean {
  return building.settings.wainscoting.height > (cutPlaneHeight ?? FLOOR_PLAN_CUT_PLANE);
}

export function makeSheetGeometryFromWainscotingCut({
  building,
  levelId,
  cutPlaneHeight,
  wallFinishCutPlaneHeight,
}: PlanGenerationContext): SheetGeometry[] {
  const resolvedCutPlaneHeight = wallFinishCutPlaneHeight ?? cutPlaneHeight;
  if (!isWainscotingAboveCutPlane(building, resolvedCutPlaneHeight)) {
    return [];
  }

  const wainscotingCuts = makeWainscotingCrossSections(building.wainscoting, levelId, true);
  const wainscotingHatches = makeWainscotingDisjointCrossSections(building.wainscoting, levelId, true);

  return [
    // hatching
    sheetPolylineGeometryNew({
      handle: building.levels[levelId].handle,
      paths: wainscotingHatches,
      ...wainscotingHatchInlineStyle,
    }),
    // cut linework
    sheetPolylineGeometryNew({
      handle: building.levels[levelId].handle,
      paths: wainscotingCuts,
    }),
  ];
}

export function makeSheetGeometryFromWainscotingProjected({
  building,
  levelId,
  cutPlaneHeight,
  wallFinishCutPlaneHeight,
}: PlanGenerationContext): SheetGeometry[] {
  const resolvedCutPlaneHeight = wallFinishCutPlaneHeight ?? cutPlaneHeight;
  const wainscotingFills = makeWainscotingDisjointCrossSections(building.wainscoting, levelId, false);
  const wainscotingProjected = makeWainscotingCrossSections(building.wainscoting, levelId, false);

  if (!isWainscotingAboveCutPlane(building, resolvedCutPlaneHeight)) {
    wainscotingProjected.push(...makeWainscotingCrossSections(building.wainscoting, levelId, true));
  }

  return [
    // masking
    sheetPolylineGeometryNew({
      handle: building.levels[levelId].handle,
      paths: wainscotingFills,
      ...wainscotingMaskInlineStyle,
    }),
    // projected linework
    sheetPolylineGeometryNew({
      handle: building.levels[levelId].handle,
      paths: wainscotingProjected,
    }),
  ];
}
